import { useQuery as useApolloQuery } from "@apollo/client/react";
import {
  GetContactsWithNoTicketsThisMonthDocument,
  GetContactsWithNoTicketsThisMonthQuery,
} from "@/src/gql/graphql";

export interface TicketFilterParams {
  first?: number;
  after?: string | null;
  /** When false, skips the Apollo query (no network fetch). Defaults to true. */
  enabled?: boolean;
}

export interface TicketFilterResult {
  nodes: NonNullable<
    NonNullable<
      NonNullable<GetContactsWithNoTicketsThisMonthQuery["contactsCollection"]>["edges"]
    >[number]["node"]
  >[];
  totalCount: number;
  pageInfo: {
    hasNextPage: boolean;
    endCursor: string | null;
  };
}

/**
 * Shared Apollo query for all contact ticket-month filter hooks.
 * Fetches all contacts + tickets and returns the raw result for client-side filtering.
 */
export function useContactTicketMonthQuery(params?: TicketFilterParams) {
  return useApolloQuery<GetContactsWithNoTicketsThisMonthQuery>(
    GetContactsWithNoTicketsThisMonthDocument,
    {
      variables: {
        first: params?.first || 1000,
        after: params?.after || null,
      },
      skip: params?.enabled === false,
      errorPolicy: "all",
      fetchPolicy: "cache-first",
    }
  );
}

/**
 * Extract and deduplicate contact IDs that have tickets within a date range.
 */
export function getContactIdsWithTicketsInRange(
  result: GetContactsWithNoTicketsThisMonthQuery,
  start: Date,
  end: Date
): Set<string> {
  const tickets =
    result.ticketsCollection?.edges
      ?.map((edge) => edge?.node)
      .filter((node): node is NonNullable<typeof node> => node != null) || [];

  return new Set(
    tickets
      .filter((ticket) => {
        if (!ticket.contact_id || !ticket.created_at) return false;
        const ticketDate = new Date(ticket.created_at);
        return ticketDate >= start && ticketDate <= end;
      })
      .map((ticket) => ticket.contact_id)
      .filter(Boolean) as string[]
  );
}

/**
 * Apply cursor-based pagination to a list of nodes and build pageInfo.
 */
export function paginateContactNodes<Node extends { id: string }>(
  nodes: Node[],
  params?: TicketFilterParams
): { nodes: Node[]; totalCount: number; pageInfo: TicketFilterResult["pageInfo"] } {
  let paginatedResults = nodes;

  if (params?.first) {
    const afterIndex = params.after
      ? nodes.findIndex((c) => c.id === params.after) + 1
      : 0;
    paginatedResults = nodes.slice(afterIndex, afterIndex + params.first);
  }

  const hasNextPage = params?.first
    ? nodes.length >
      (params.after ? nodes.findIndex((c) => c.id === params.after) + 1 : 0) +
        (params.first || 0)
    : false;

  const endCursor =
    paginatedResults.length > 0 ? paginatedResults[paginatedResults.length - 1].id : null;

  return {
    nodes: paginatedResults,
    totalCount: nodes.length,
    pageInfo: { hasNextPage, endCursor },
  };
}
